'use client';

import type { ProductCardProps } from './data';

import {
  Box,
  Counter,
  Image,
  Link,
  Stack,
  Tag,
  TagLabel,
  Text,
} from '@/components';
import { useTranslation } from '@/hooks';
import { toToman } from '@/utils';

const ProductCard: React.FC<ProductCardProps> = ({ cardData, link, bg }) => {
  const { t } = useTranslation('components.productCard');

  const hasDiscount =
    cardData.pricing && cardData.pricing.payable_price !== cardData.price;

  return (
    <Box position="relative" w="full">
      <Link href={link} w="full" h="full" display="flex">
        <Stack
          role="group"
          cursor="pointer"
          width="100%"
          borderRadius="xs"
          position="relative"
          bg="white"
          gap={0}
        >
          <Stack position="absolute" left={0} top={0} zIndex={1}>
            {cardData.tags &&
              cardData.tags.length > 0 &&
              cardData.tags.map((tag) => (
                <Tag key={tag.id} size={{ base: 'sm', md: 'md' }}>
                  <TagLabel>{tag.title}</TagLabel>
                </Tag>
              ))}
          </Stack>
          <Stack
            direction="column"
            position="absolute"
            right="0"
            top="0"
            zIndex={1}
            gap="7px"
          >
            {cardData.pricing &&
              cardData.pricing.discounts.length > 0 &&
              cardData.pricing.discounts.map(
                (discount) =>
                  discount.can_show && (
                    <Tag
                      key={discount.title}
                      size={{ base: 'sm', md: 'md' }}
                      variant="promo"
                      bg={
                        discount.type === 'extra_retail_percent'
                          ? 'secondary.500'
                          : 'complimentary.4'
                      }
                      h={{ base: '24px', md: '34px' }}
                      w={{ base: '50px', md: '70px' }}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius={0}
                      borderTopRightRadius={
                        discount.type === 'extra_retail_percent' ? 'xs' : 'none'
                      }
                    >
                      <TagLabel color="text" fontWeight="bold" h={4}>
                        {discount.percent}%
                      </TagLabel>
                    </Tag>
                  )
              )}
            )
          </Stack>
          <Stack
            overflow="hidden"
            borderRadius="xs"
            bg={bg || 'secondary.50'}
            justifyContent="center"
            alignItems="center"
            px={2.5}
            py={5}
            aspectRatio={1}
            sx={{
              '.img': {
                transition: 'transform 1s',
                objectFit: 'cover',
              },
            }}
            _groupHover={{
              md: { '.img': { transform: 'scale(1.1)' } },
            }}
          >
            <Image
              src={cardData.home_page_image}
              alt={cardData?.alt_home_page_image}
              width={264}
              height={264}
              className="img"
            />
          </Stack>
          <Stack
            w="full"
            direction="column"
            justify="space-between"
            alignItems="center"
            py={4}
            pb={3}
            gap={{ base: 1, md: 4 }}
          >
            <Box maxW="300px">
              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="700"
                dir="rtl"
                noOfLines={2}
                minH="48px"
                lineHeight="24px"
                textAlign="center"
              >
                {cardData.title}
              </Text>
            </Box>
            <Text fontSize={{ base: 'sm', md: 'md' }} fontWeight="700">
              {`${cardData.measurement_value} ${cardData.measurement_type === 'gr' ? t('gram') : t('mil')}`}
            </Text>
            <Stack
              direction="row"
              justify="space-between"
              alignItems="center"
              h="42px"
            >
              <Box
                display="flex"
                justifyContent="flex-end"
                w="full"
                flexGrow="1"
              >
                {cardData.inventory.is_available || cardData.type === 'view' ? (
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    gap={{ base: '0', md: '2.5' }}
                    align="flex-end"
                  >
                    {hasDiscount && (
                      <Text color="primary.200" textDecor="line-through">
                        {toToman(cardData.price)}
                      </Text>
                    )}
                    <Text
                      fontSize={hasDiscount ? 'sm' : 'md'}
                      fontWeight="700"
                      color={hasDiscount ? 'error.500' : 'text'}
                    >
                      {toToman(
                        cardData.pricing
                          ? cardData.pricing.payable_price
                          : cardData.price
                      )}
                    </Text>
                  </Stack>
                ) : (
                  <Text
                    color="gray.600"
                    fontWeight={700}
                    fontSize={{ base: 'md', xl: 'xl' }}
                  >
                    {t('notAvailable')}
                  </Text>
                )}
              </Box>
            </Stack>
            <Box h={{ base: '34px', md: '46px' }} />
          </Stack>
        </Stack>
      </Link>
      <Box
        position="absolute"
        bottom={3}
        w="full"
        display="flex"
        justifyContent="center"
      >
        <Counter product={cardData} />
      </Box>
    </Box>
  );
};
export default ProductCard;
